import React from 'react';

function ChildLearningAnalytics({ child, profile, detailedView = false, onGeneratePrediction, onGenerateStudyPlan, onClose }) {
  if (!child || !profile) {
    return (
      <div className="alert alert-info">
        No learning data available for this child.
      </div>
    );
  }

  return (
    <div className="learning-analytics">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="font-weight-bold text-gray-800">
          {child.firstName} {child.lastName} - Learning Analytics
        </h5>
        {detailedView && onClose && (
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            <i className="fas fa-times me-2"></i>Close
          </button>
        )}
      </div>

      {/* Basic Info */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card">
            <div className="card-body text-center">
              <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mx-auto mb-3"
                   style={{width: '60px', height: '60px'}}>
                {child.firstName?.charAt(0) || 'C'}
              </div>
              <h6 className="font-weight-bold">{child.firstName} {child.lastName}</h6>
              <p className="text-muted mb-0">{child.grade}</p>
            </div>
          </div>
        </div>
        
        <div className="col-md-9">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <h6 className="text-muted">Learning Style</h6>
                  <p className="font-weight-bold">{child.learningStyle || 'Not specified'}</p>
                </div>
                <div className="col-md-4">
                  <h6 className="text-muted">Subjects</h6>
                  <p className="font-weight-bold">{(child.subjects || []).join(', ') || 'None specified'}</p>
                </div>
                <div className="col-md-4">
                  <h6 className="text-muted">Learning Goals</h6>
                  <p className="font-weight-bold">{child.learningGoals || 'Not specified'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="card shadow mb-4">
        <div className="card-header">
          <h6 className="m-0 font-weight-bold text-primary">Performance Summary</h6>
        </div>
        <div className="card-body">
          {profile.overallScore ? (
            <div className="row">
              <div className="col-md-6">
                <div className="text-center mb-4">
                  <div className="display-4 text-primary">{profile.overallScore}%</div>
                  <p className="text-muted">Overall Performance</p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <h6>Subject Performance</h6>
                  {profile.subjectPerformance && Object.entries(profile.subjectPerformance).map(([subject, score], index) => (
                    <div key={index} className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span>{subject}</span>
                        <span>{score}%</span>
                      </div>
                      <div className="progress" style={{height: '8px'}}>
                        <div 
                          className={`progress-bar ${score >= 80 ? 'bg-success' : score >= 60 ? 'bg-warning' : 'bg-danger'}`}
                          style={{width: `${score}%`}}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted">No performance data available yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card shadow mb-4">
        <div className="card-header">
          <h6 className="m-0 font-weight-bold text-primary">Quick Actions</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <button 
                className="btn btn-dark btn-block mb-2"
                onClick={onGeneratePrediction}
              >
                <i className="fas fa-robot me-2"></i>
                Generate AI Prediction
              </button>
              <p className="text-muted small">
                Get AI-powered insights into future performance
              </p>
            </div>
            <div className="col-md-6">
              <button 
                className="btn btn-secondary btn-block mb-2"
                onClick={onGenerateStudyPlan}
              >
                <i className="fas fa-calendar-alt me-2"></i>
                Create Study Plan
              </button>
              <p className="text-muted small">
                Generate personalized study schedule
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Observations */}
      {profile.recentObservations && profile.recentObservations.length > 0 && (
        <div className="card shadow">
          <div className="card-header">
            <h6 className="m-0 font-weight-bold text-primary">Recent Teacher Observations</h6>
          </div>
          <div className="card-body">
            <div className="list-group list-group-flush">
              {profile.recentObservations.slice(0, 5).map((observation, index) => (
                <div key={index} className="list-group-item">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="mb-1">{observation.teacherName || 'Teacher'}</h6>
                      <p className="mb-1">{observation.comment}</p>
                      <small className="text-muted">
                        {observation.date ? new Date(observation.date).toLocaleDateString() : ''}
                      </small>
                    </div>
                    <span className={`badge bg-${observation.type === 'positive' ? 'success' : 'warning'}`}>
                      {observation.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChildLearningAnalytics;